<template>
    <div>
        <div v-if="seeReport">
            <vs-row>
                <vs-col vs-xs="12" vs-sm="4" vs-lg="4">
                    <SelectDate 
                        class="w-full my-3 mb-3" 
                        :startDate.sync="from_date" 
                        :endDate.sync="to_date"
                        :isRange="true"
                        label="Date Range" 
                    />
                </vs-col>
                <vs-col vs-xs="12" vs-sm="4" vs-lg="4">
                    <InputSelect
                        :model.sync="report_"
                        placeholder="Select Report"
                        container_class="w-full my-3 mb-3"
                        :options="report_options"
                        label="Report"
                    />
                </vs-col>
                <vs-col vs-xs="12" vs-sm="4" vs-lg="4">
                    <InputSelect 
                        :model.sync="package_"
                        placeholder="Select Package"
                        container_class="w-full my-3 mb-3"
                        :options="package_options"
                        label="Package"
                        :disabled="!report_"
                    />
                </vs-col>
            </vs-row>
    
            <template v-if="loading">
                <vx-card>
                    <h3>Loading Data.. Please wait..</h3>
                </vx-card>
            </template>
    
            <template v-else>
                <div style="display: flex; align-items: center; justify-content: center;">
                    <h4 class="" v-if="report">{{ report.name }} (Daily Report)</h4>
                    <vx-tooltip text="Create Excel" color="primary" class="ml-2">
                        <vs-button size="small" color="primary" type="filled" @click="create_excel"  class="ml-2" :disabled="loading">
                            <font-awesome-icon :icon="excel" />
                        </vs-button>
                    </vx-tooltip>
                </div>
    
                <template v-if="ongoing_exports.length">
                    <vs-alert color="primary" v-if="loading_exports">
                        Loading Latest Exports, please wait..
                    </vs-alert>
                    <template v-else>
                        <template v-for="(item, index) in ongoing_exports">
                            <vs-alert color="primary" :key="index" class="mb-2">
                                <template v-if="item.status == 'p'">
                                    Your export is pending, please check back in few minutes
                                </template>
                                <template v-else-if="item.status == 's'">
                                    Your export is in queue, please check back in few minutes
                                </template>
                                <template v-else-if="item.status == 'r'">
                                    Your export is being generated, please check back in few minutes
                                </template>
                                <template v-else-if="item.status == 'c'">
                                    Your export has been completed. (<FormatText :text="item.created_on" format="datetime" />) ({{ item.params.schedule_name }}/{{ item.params.package_name }}) <a :href="item.export_excel" target="_blank" style="float:right;"><vs-button size="small">Download</vs-button></a>
                                </template>
                                <template v-else-if="item.status == 'f'">
                                    Your export has been failed, please request again!
                                </template>
                            </vs-alert>
                        </template>
                    </template>
                </template>
    
                <vs-row>
                    <vs-col>
                        <vs-table class="freeze_row row2 row3_top_diff" hoverFlat :data="package_keys" noDataText="No Parameters defined for this package">
                            
                            <tr>
                                <vs-th rowspan="2" style="min-width: 50px;">BOQ</vs-th>
                                <vs-th rowspan="2" style="min-width: 190px;">Description of Work</vs-th>
                                <vs-th rowspan="2" style="min-width: 80px;">UOM</vs-th>
                                <vs-th rowspan="2" style="min-width: 80px;">Month Qty</vs-th>
                                <vs-th colspan="4">Median</vs-th>
                                <vs-th rowspan="2" style="min-width: 140px;">Remarks</vs-th>
                                <vs-th rowspan="2" style="min-width: 70px;">Verified</vs-th>
                                <vs-th rowspan="2" style="min-width: 70px;">Status</vs-th>
                                <vs-th rowspan="2" style="min-width: 120px;">Created By</vs-th>
                                <vs-th rowspan="2" style="min-width: 100px;">Closed Status</vs-th>
                                <vs-th rowspan="2" style="min-width: 120px;">Closed On</vs-th>
                                <vs-th rowspan="2" style="min-width: 150px;">Upto Previous Day</vs-th>
                                <vs-th rowspan="2" style="min-width: 100px;">Today</vs-th>
                                <vs-th rowspan="2" style="min-width: 180px;">Total Done for the Month</vs-th>
                                <vs-th rowspan="2" style="min-width: 180px;">Total Target for the Month</vs-th>
                                <vs-th rowspan="2" style="min-width: 100px;">Weightage %</vs-th>
                                <vs-th rowspan="2" style="min-width: 180px;">% Achieved to Date</vs-th>
                            </tr>
                            <tr>
                                <vs-th style="min-width: 100px;">Direction</vs-th>
                                <vs-th style="min-width: 80px;">From</vs-th>
                                <vs-th style="min-width: 80px;">To</vs-th>
                                <vs-th style="min-width: 80px;">Qty</vs-th>
                            </tr>
    
                            <template v-for="(report_key, index) in package_keys">
                                <template v-for="subindex in data[get_key(report_key.id)].length">
                                    <vs-tr :key="`row-${index}-${subindex}`">
                                        <template v-if="report_key.is_heading">
                                            <vs-td class="font-bold">
                                                {{ report_key.boq_no.replace(report_key.parent_key ? `${report_key.parent_key.boq_no}.` : "", "") }}
                                            </vs-td>
                                            <vs-td class="font-bold">
                                                {{ report_key.description }}
                                            </vs-td>
                                            <vs-td colspan="18"></vs-td>
                                        </template>
                                        <template v-else>
                                            <vs-td :class="{'font-bold': report_key.is_heading}" v-if="subindex == 1" :rowspan="data[get_key(report_key.id)].length">
                                                {{ report_key.boq_no.replace(report_key.parent_key ? `${report_key.parent_key.boq_no}.` : "", "") }}
                                            </vs-td>
                                            <vs-td :class="{'font-bold': report_key.is_heading}" v-if="subindex == 1" :rowspan="data[get_key(report_key.id)].length">
                                                {{ report_key.description }}
                                            </vs-td>
                                            <vs-td v-if="subindex == 1" :rowspan="data[get_key(report_key.id)].length">
                                                {{ optional_month_data_params(report_key.id, 'unit_of_measurement', '') }}
                                            </vs-td>
                                            <vs-td v-if="subindex == 1" :rowspan="data[get_key(report_key.id)].length">
                                                {{ month_data[get_key(report_key.id)] ? month_data[get_key(report_key.id)].target_value : '' }}
                                            </vs-td>
                                            <vs-td>
                                                <template v-if="data[get_key(report_key.id)][subindex - 1].data.median.direction">
                                                    <FormatText :text="data[get_key(report_key.id)][subindex - 1].data.median.direction" format="select" :kwargs="direction_options" />
                                                </template>
                                                <template v-else>
                                                    {{ '' }}
                                                </template>
                                            </vs-td>
                                            <vs-td>
                                                {{ data[get_key(report_key.id)][subindex - 1].data.median.from }}
                                            </vs-td>
                                            <vs-td>
                                                {{ data[get_key(report_key.id)][subindex - 1].data.median.to }}
                                            </vs-td>
                                            <vs-td>
                                                {{ data[get_key(report_key.id)][subindex - 1].value }}
                                            </vs-td>
                                            <vs-td>
                                                {{ data[get_key(report_key.id)][subindex - 1].remarks }}
                                            </vs-td>
                                            <vs-td>
                                                <template v-if="data[get_key(report_key.id)][subindex - 1].verification_status == 'p'">
                                                    <vx-tooltip text= "Pending" color="warning">
                                                        <font-awesome-icon class="text-warning" :icon="pending" />
                                                    </vx-tooltip>
                                                </template>
                                                <template v-else-if="data[get_key(report_key.id)][subindex - 1].verification_status == 'a'">
                                                    <template v-if="data[get_key(report_key.id)][subindex - 1].verified_by">
                                                        <vx-tooltip 
                                                            :text="`Approved By: ${data[get_key(report_key.id)][subindex - 1].verified_by.first_name || ''} ${data[get_key(report_key.id)][subindex - 1].verified_by.last_name || ''}`" 
                                                            color="success">
                                                            <font-awesome-icon class="text-success" :icon="approved" />
                                                        </vx-tooltip>
                                                    </template>
                                                    <template v-else>
                                                        {{ '' }}
                                                    </template>
                                                </template>
                                                <template v-else>
                                                    <template v-if="data[get_key(report_key.id)][subindex - 1].verified_by">
                                                        <vx-tooltip 
                                                        :text="`Rejected By: ${data[get_key(report_key.id)][subindex - 1].verified_by.first_name || ''} ${data[get_key(report_key.id)][subindex - 1].verified_by.last_name || ''}`" 
                                                        color="danger">
                                                            <font-awesome-icon class="text-danger" :icon="rejected" />
                                                        </vx-tooltip>
                                                    </template>
                                                    <template v-else>
                                                        {{ '' }}
                                                    </template>
                                                </template>
                                            </vs-td>
                                            <vs-td>
                                                <template v-if="data[get_key(report_key.id)][subindex - 1].status == 'p'">
                                                    <vx-tooltip text="Pending" color="warning">
                                                        <font-awesome-icon class="text-warning" :icon="pending" />
                                                    </vx-tooltip>
                                                </template>
                                                <template v-else-if="data[get_key(report_key.id)][subindex - 1].status == 'a'">
                                                    <template v-if="data[get_key(report_key.id)][subindex - 1].reviewed_by">
                                                        <vx-tooltip 
                                                        :text="`Approved By: ${data[get_key(report_key.id)][subindex - 1].reviewed_by.first_name || ''} ${data[get_key(report_key.id)][subindex - 1].reviewed_by.last_name || ''}`" 
                                                        color="success">
                                                            <font-awesome-icon class="text-success" :icon="approved" />
                                                        </vx-tooltip>
                                                    </template>
                                                    <template v-else>
                                                        {{ '' }}
                                                    </template>
                                                </template>
                                                <template v-else>
                                                    <template v-if="data[get_key(report_key.id)][subindex - 1].reviewed_by">
                                                        <vx-tooltip 
                                                        :text="`Rejected By: ${data[get_key(report_key.id)][subindex - 1].reviewed_by.first_name || ''} ${data[get_key(report_key.id)][subindex - 1].reviewed_by.last_name || ''}`" 
                                                        color="danger">
                                                            <font-awesome-icon class="text-danger" :icon="rejected" />
                                                        </vx-tooltip>
                                                    </template>
                                                    <template v-else>
                                                        {{ '' }}
                                                    </template>
                                                </template>
                                            </vs-td>
                                            <vs-td>
                                                <template v-if="data[get_key(report_key.id)][subindex - 1].added_by">
                                                    {{ data[get_key(report_key.id)][subindex - 1].added_by.first_name || ''}} {{data[get_key(report_key.id)][subindex - 1].added_by.last_name || '' }}
                                                </template>
                                                <template v-else>
                                                    {{ '' }}
                                                </template>
                                            </vs-td>
                                            <vs-td>
                                                <template v-if="data[get_key(report_key.id)][subindex - 1].closed_status == 'r'">
                                                    <vx-tooltip text="Rejected" color="danger">
                                                        <font-awesome-icon class="text-danger" :icon="rejected" />
                                                    </vx-tooltip>
                                                </template>
                                                <template v-else-if="data[get_key(report_key.id)][subindex - 1].closed_status == 'c'">
                                                    <vx-tooltip text="Completed" color="success">
                                                        <font-awesome-icon class="text-success" :icon="approved" />
                                                    </vx-tooltip>
                                                </template>
                                                <template v-else>
                                                    <vx-tooltip text="Pending" color="warning">
                                                        <font-awesome-icon class="text-warning" :icon="pending" />
                                                    </vx-tooltip>
                                                </template>
                                            </vs-td>
                                            <vs-td>
                                                <template v-if="data[get_key(report_key.id)][subindex - 1].closed_on">
                                                    <FormatText :text="data[get_key(report_key.id)][subindex - 1].closed_on" format="datetime" />
                                                </template>
                                                <template v-else>
                                                    -
                                                </template>
                                            </vs-td>
                                            <template v-if="month_data[get_key(report_key.id)]">
                                                <vs-td v-if="subindex == 1" :rowspan="data[get_key(report_key.id)].length">
                                                    {{ month_data[get_key(report_key.id)].total_qty_till_date || 0 }}
                                                </vs-td>
                                                <vs-td v-if="subindex == 1" :rowspan="data[get_key(report_key.id)].length">
                                                    {{ month_data[get_key(report_key.id)].work_done_today || 0 }}
                                                </vs-td>
                                                <vs-td v-if="subindex == 1" :rowspan="data[get_key(report_key.id)].length">
                                                    {{ (month_data[get_key(report_key.id)].total_qty_till_date || 0) + (month_data[get_key(report_key.id)].work_done_today || 0) }}
                                                </vs-td>
                                                <vs-td v-if="subindex == 1" :rowspan="data[get_key(report_key.id)].length">
                                                    {{ month_data[get_key(report_key.id)].target_value*(optional_month_data_params(report_key.id, 'frequency', 0)) }}
                                                </vs-td>
                                                <vs-td v-if="subindex == 1" :rowspan="data[get_key(report_key.id)].length">
                                                    {{ optional_month_data_params(report_key.id, 'weightage', 0) }}
                                                </vs-td>
                                                <vs-td v-if="subindex == 1" :rowspan="data[get_key(report_key.id)].length">
                                                    {{ (((month_data[get_key(report_key.id)].total_qty_till_date || 0) + (month_data[get_key(report_key.id)].work_done_today || 0)) 
                                                    / (month_data[get_key(report_key.id)].target_value*(optional_month_data_params(report_key.id, 'frequency', 0))) *
                                                    optional_month_data_params(report_key.id, 'weightage', 0)).toFixed(3)
                                                    }} %
                                                </vs-td>
                                            </template>
                                            <template v-else>
                                                <vs-td> {{ '' }}</vs-td>
                                                <vs-td> {{ '' }}</vs-td>
                                                <vs-td> {{ '' }}</vs-td>
                                                <vs-td> {{ '' }}</vs-td>
                                                <vs-td> {{ '' }}</vs-td>
                                                <vs-td> {{ '' }}</vs-td>
                                            </template>
                                        </template>
                                    </vs-tr>
                                </template>
                            </template> 
                        </vs-table>
                    </vs-col>
                </vs-row>
            </template>
        </div>
        <div v-else>
            <PermissionDenied />
        </div>
    </div>
</template>

<script>
import { faPlus, faClock, faCheckCircle, faTimesCircle, faFileExcel  } from '@fortawesome/free-solid-svg-icons';
import { Request } from '../../../utils/requests';
import moment from 'moment';

export default {
    props: {
        report_mode: {
            type: Boolean,
            required: false,
        }
    },
    data() {
        return {

            date: moment().format('YYYY-MM-DD'),
            data: {},
            month_data: {},

            packages: [],

            // package
            report_: null,
            package_: null,
            package_keys: [],

            selected_rows: [],
            api_errors: {},

            loading_modules: {
                packages: false,
                package_keys: false,
                package_data: false
            },

            direction_options: [
                {value: 'median', label: "Median"},
                {value: 'bhs', label: "BHS"},
                {value: 'rhs', label: "RHS"},
                {value: 'lhs', label: "LHS"},
            ],

            loading_exports: false,
            ongoing_exports: [],
            from_date: moment().format('YYYY-MM-DD'),
            to_date: moment().format('YYYY-MM-DD'),

        }
    },
    computed: {
        addIcon: () => faPlus,
        pending: () => faClock,
        approved: () => faCheckCircle,
        rejected: () => faTimesCircle,
        excel: () => faFileExcel,
        year_options: () => {
            let years = []
            for (let i=new Date().getFullYear(); i>=1990; i--) {
                years.push({value: i, label: i.toString()})
            }

            return years
        },
        package_options() {
            return this.packages.map(v => {
                return {
                    value: v.id,
                    label: v.name
                }
            })
        },
        loading() {
            return Object.keys(this.loading_modules).filter(v => this.loading_modules[v] == true).length > 0
        },
        report_options() {
            return this.$store.state.organisation.dpr_reports.filter(v => v.issue_type == this.$route.params.issue_type).map(
                v => {
                    return {
                        label: v.name,
                        value: v.id
                    }
                }
            )
        },
        report() {
            if (!this.report_) return null
            let matches = this.$store.state.organisation.dpr_reports.filter(v => v.id == this.report_)
            if (matches.length) return matches[0]
            else return null
        },
        seeReport() {
            return this.hasAccess('read', `dpr_log_daily_report`, 'all')
        }
    },
    watch: {
        from_date (newV, oldV) {
            if (newV == oldV) return
            this.load_data()
        },
        to_date (newV, oldV) {
            if (newV == oldV) return
            this.load_data()
        },
        package_ (newV, oldV) {
            if (newV == oldV) return
            this.load_data()
        },
        report_ (newV, oldV) {
            if (newV == oldV) return
            this.init()
            this.refresh_exports()
        }
    },
    mounted() {
        if (this.report_options.length) {
            this.report_ = this.report_options[0].value
        }
    },
    beforeDestroy() {
        if (this.export_timer) clearTimeout(this.export_timer)
    },
    methods: {
        init() {
            this.load_packages()
        },
        load_packages() {
            if (!this.report_) return
            this.loading_modules.packages = true
            Request(this, 'GET', this.$store.state.apiendpoints.dpr_packages, {report: this.report_, limit: 100}).then(
                res => {
                    this.packages = res.results
                    this.loading_modules.packages = false
                    
                    if (this.packages.length)
                        this.package_ = this.packages[0].id
                },
                err => {
                    this.loading_modules.packages = false
                    this.$vs.notify({
                        time: 3000,
                        title: 'Error',
                        text: "An unknown error occurred while loading available packages! Please try again!",
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    })
                    console.error(err)
                }
            )
        },
        load_data() {
            this.load_dpr_keys()
            this.load_dpr_data()
            this.load_dpr_month_data()
        },
        load_dpr_keys() {
            this.loading_modules.package_keys = true
            let url = `${this.$store.state.apiendpoints.dpr_package_keys}`

            let payload = {
                package: this.package_,
                limit: 100
            }

            Request(this, 'GET', url, payload).then(
                res => {
                    this.loading_modules.package_keys = false
                    this.package_keys = res.results
                },
                err => {
                    this.loading_modules.package_keys = false
                    this.package_keys = []
                    this.$vs.notify({
                        time: 3000,
                        title: 'Error',
                        text: "An unknown error occurred while loading available report parameters! Please try again!",
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    })
                }
            )
        },
        load_dpr_month_data() {
            this.loading_modules.package_data = true

            let url = `${this.$store.state.apiendpoints.dpr_month}`
            let payload = {
                report_mode: true,
                date: this.from_date,
                to_date: this.to_date,
                package: this.package_,
                is_month_report: true
            }

            Request(this, 'GET', url, payload).then(
                res => {
                    this.loading_modules.package_data = false
                    this.month_data = res
                },
                err => {
                    this.loading_modules.package_data = false
                    this.month_data = {}
                }
            )
        },
        load_dpr_data() {
            this.loading_modules.package_data = true

            let url = `${this.$store.state.apiendpoints.dpr_daily}`
            let payload = {
                date: this.from_date,
                to_date: this.to_date,
                package: this.package_
            }

            Request(this, 'GET', url, payload).then(
                res => {
                    this.loading_modules.package_data = false
                    this.data = res
                    this.refresh_exports()
                },
                err => {
                    this.loading_modules.package_data = false
                    this.data = {}
                }
            )
        },
        add_empty_row(key) {
            this.data[key].push({
                remarks: null,
                value: null,
                status: null,
                verification_status: null,
                data: {
                    median: {
                        direction: null,
                        from: null,
                        to: null
                    }
                }
            })
        },
        get_key(key) {
            if (!this.data[key]) {
                this.$set(this.data, key, [])
                this.add_empty_row(key)
            }
            return key
        },
        create_excel() {

            this.$vs.loading();

            let payload = {
                module_key: 'd',
                params: {
                    "issue_type": this.$route.params.issue_type,
                    "rm_type": 'daily',
                    "filters": {},
                    'package': this.package_,
                    'schedule_name': this.report.name,
                    'package_name': this.package_options.filter(v => v.value == this.package_)[0].label,
                    'date': this.from_date,
                    'to_date': this.to_date
                }
            }

            let url = this.$store.state.apiendpoints.excel_create

            Request(this, 'POST', url, null, payload).then( 
                (res) => {
                    
                    this.$vs.loading.close();
                    this.refresh_exports()

                    this.$vs.notify({
                        time: 3000,
                        title: 'Success',
                        text: "Your request to download file has been submitted",
                        iconPack: 'feather',
                        icon: 'icon-check-circle',
                        color: 'success'
                    })
                }, 
                (err) => {
    
                    this.$vs.loading.close();
                    if (err.error) {
                        this.api_errors = err.error

                        this.$vs.notify({
                            time: 2500,
                            title: 'Error',
                            text: "Please fix the errors and try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    } else {
                        this.$vs.notify({
                            time: 2500,
                            title: 'Error',
                            text: "Please try again later.",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    }
                }
            )
        },
        refresh_exports() {
            this.loading_exports = true

            let url = this.$store.state.apiendpoints.excel_list

            let payload = {
                created_on: moment().format('YYYY-MM-DD'),
                module_key: 'd',
                params__issue_type: this.$route.params.issue_type,
                params__rm_type: 'daily',
                params__schedule_name: this.report.name,
                params__package_name: this.package_options.filter(v => v.value == this.package_)[0].label,
                'date': this.from_date,
                'to_date': this.to_date,
                limit: 4
            }

            Request(this, 'GET', url, payload).then( 
                (res) => {
                    this.ongoing_exports = res.results
                    this.loading_exports = false
                    if(this.ongoing_exports.filter((item) => ['p', 's', 'r'].includes(item.status)).length != 0) this.auto_refresh_export()
                }, 
                (err) => {
                    this.loading_exports = false;
                    this.$vs.notify({
                        time: 2500,
                        title: 'Error',
                        text: "Unable to load current exports, please refresh to try again!",
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    })
                }
            )
        },
        auto_refresh_export() {
            this.export_timer = setTimeout(() => {
                this.refresh_exports()
            }, 5000);
        },
        optional_month_data_params(report_key, lookup_key, default_value) {
            return this.month_data[this.get_key(report_key)] && this.month_data[this.get_key(report_key)].params ? this.month_data[this.get_key(report_key)].params[lookup_key] : default_value
        }
    }
}
</script>

<style lang="scss">
.table-fix {
    .vs-table--tbody {
        padding-bottom: 100px;
        background: #fff;
        border: 0;
    }
}
</style>